import { Box, useTheme } from '@mui/material';
import {
  type GridColDef,
  type GridFilterItem,
  type GridFilterModel,
  type GridPaginationModel,
  type GridPreferencePanelParams,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  useAppDispatch,
  useAppSelector,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '@verantos-dev/core-annotation-tool';
import { DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import { IRRDataGridToolbar } from './IRRDataGridtoolbar';
import { useIRRScoreDatagridFetch } from '../../hook/useIRRScoreDataGridFetch';
import {
  clearIRRCardFilters,
  setIRRCardFilters,
  setIRRScoreTablePaginationModel,
} from '../../state/metricsSlice';
import { selectIRRScoreTablePage, selectIRRScoreTablePageSize } from '../../state/selector';
import { IRRScoreDataItem } from '../../types/responseTypes';

// Apply a single MUI DataGrid filter item to a row (supports partial string matching).
function matchesFilterItem(row: IRRScoreDataItem, item: GridFilterItem): boolean {
  const raw = row[item.field as keyof IRRScoreDataItem];
  const cellVal = String(raw ?? '').toLowerCase();
  const filterVal = String(item.value ?? '').toLowerCase();
  if (!filterVal) return true;
  switch (item.operator) {
    case 'equals':
      return cellVal === filterVal;
    case 'startsWith':
      return cellVal.startsWith(filterVal);
    case 'endsWith':
      return cellVal.endsWith(filterVal);
    case 'contains':
    default:
      return cellVal.includes(filterVal);
  }
}

// Fields that drive a backend re-query for the F1 cards (concept name maps to conceptId).
const BACKEND_FILTER_FIELDS = new Set([
  'annotator1',
  'annotator2',
  'conceptId',
  'conceptName',
  'entityId',
]);

export const IRRScoreDataTable = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const irrScoreTablePage = useAppSelector(selectIRRScoreTablePage);
  const irrScoreTablePageSize = useAppSelector(selectIRRScoreTablePageSize);

  // Track filter model in a ref so onPreferencePanelClose can read it without
  // re-rendering and without interfering with the DataGrid wrapper's own handler.
  const filterModelRef = useRef<GridFilterModel>({ items: [] });
  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    filterModelRef.current = model;
  }, []);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setIRRScoreTablePaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const fetchResult = useIRRScoreDatagridFetch();
  const { isFetching, data: fetchedData } = fetchResult || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(setIRRScoreTablePaginationModel({ page: 0, pageSize: irrScoreTablePageSize }));
      dispatch(clearIRRCardFilters());
    }
  }, [isFetching, dispatch]);

  // When the filter panel closes, apply the current filter model against the raw
  // data to find matching rows, then extract exact values for backend query params.
  // Handles partial string matching and concept-name → concept-id mapping.
  const handlePreferencePanelClose = useCallback(
    (params: GridPreferencePanelParams) => {
      if (params.openedPanelValue !== GridPreferencePanelsValue.filters) return;

      const model = filterModelRef.current;
      const activeItems = model.items.filter(
        (item) => BACKEND_FILTER_FIELDS.has(item.field) && item.value != null && item.value !== ''
      );

      if (activeItems.length === 0) {
        dispatch(clearIRRCardFilters());
        return;
      }

      const allRows: IRRScoreDataItem[] = fetchedData?.data ?? [];

      // Apply ALL active filter items (AND logic, same as DataGrid default).
      const filtered = allRows.filter((row) =>
        model.items.every((item) => (!item.value ? true : matchesFilterItem(row, item)))
      );

      const unique = (field: keyof IRRScoreDataItem) => [
        ...new Set(filtered.map((r) => r[field] as string).filter(Boolean)),
      ];

      const hasAnnotator1 = activeItems.some((i) => i.field === 'annotator1');
      const hasAnnotator2 = activeItems.some((i) => i.field === 'annotator2');
      const hasConceptFilter = activeItems.some(
        (i) => i.field === 'conceptId' || i.field === 'conceptName'
      );
      const hasEntityId = activeItems.some((i) => i.field === 'entityId');

      dispatch(
        setIRRCardFilters({
          annotator1: hasAnnotator1 ? unique('annotator1') : null,
          annotator2: hasAnnotator2 ? unique('annotator2') : null,
          // Both conceptId and conceptName filters resolve to conceptId values for the backend.
          conceptIds: hasConceptFilter ? unique('conceptId') : null,
          entityIds: hasEntityId ? unique('entityId') : null,
        })
      );
    },
    [fetchedData, dispatch]
  );

  const columns: GridColDef<IRRScoreDataItem>[] = useMemo(
    () => [
      {
        field: 'srNo',
        headerName: 'Sr No.',
        sortable: false,
        filterable: false,
        minWidth: 100,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (_value: unknown, row: IRRScoreDataItem, _column, apiRef) =>
          apiRef.current.getRowIndexRelativeToVisibleRows(row.id) + 1,
      },
      {
        field: 'entityId',
        headerName: 'Encounter ID / Note ID',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'conceptId',
        headerName: 'Concept ID',
        minWidth: 120,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'conceptName',
        headerName: 'Concept name',
        minWidth: 300,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
            title={params.value}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'annotator1',
        headerName: 'Annotator 1',
        minWidth: 150,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'annotator2',
        headerName: 'Annotator 2',
        minWidth: 150,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'initialTp',
        headerName: 'Initial TP',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'initialFp',
        headerName: 'Initial FP',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'initialFn',
        headerName: 'Initial FN',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentTp',
        headerName: 'Current TP',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentFp',
        headerName: 'Current FP',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentFn',
        headerName: 'Current FN',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'initialF1Measure',
        headerName: 'Initial F1',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'currentF1Measure',
        headerName: 'Current F1',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
    ],
    []
  );

  return (
    <Box>
      <DataGridLayout id="metrics-irr-score-data-grid">
        <DataGrid
          id="metrics-irr-score-data-grid"
          mode="client"
          pagination
          loadOnFetch
          pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
          paginationModel={{ page: irrScoreTablePage, pageSize: irrScoreTablePageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          disableSearchParamSync
          preventUrlReadWhenSyncDisabled
          getRowId={(row) => row.id}
          columns={columns}
          slots={{ toolbar: IRRDataGridToolbar }}
          useFetchDataGridData={useIRRScoreDatagridFetch}
          onFilterModelChange={handleFilterModelChange}
          onPreferencePanelClose={handlePreferencePanelClose}
          initialState={{
            pinnedColumns: {
              left: ['srNo', 'entityId'],
            },
          }}
          sx={{
            maxHeight: '70vh',
            '& .MuiDataGrid-row': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              borderRight: `2px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: '1.2',
              textAlign: 'center',
            },

            '& .MuiDataGrid-columnHeader': {
              height: 'auto !important',
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
